/**
 * Organizations Page
 *
 * Admin-only page for managing organizations with full CRUD functionality.
 * Features:
 * - List of organizations (table showing name, slug, status, created date)
 * - "Create Organization" button that opens a modal form
 * - Edit/delete actions per organization
 * - Create form: name and slug inputs
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Container } from '../components/layout';
import { organizationsService } from '../services/organizationsService';
import { logger } from '../services/loggerService';
import { useAuth } from '../contexts/AuthContext';
import { Toast } from '../components/feedback';
import { Button, Badge } from '../components/ui';
import { getStatusBadgeVariant } from '../components/ui/Badge';
import type { Organization, CreateOrganizationDto, UpdateOrganizationDto } from '../services/organizationsService';
import type { ToastType } from '../components/feedback';

/**
 * Toast state interface
 */
interface ToastState {
  message: string;
  type: ToastType;
}

/**
 * Format date as DD/MM/YYYY with leading zeros
 */
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

/**
 * Generate a slug from a name string
 */
const generateSlug = (name: string): string => {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
};

/**
 * OrganizationsPage Component
 *
 * Manage organizations: create, list, edit, and delete.
 */
export const OrganizationsPage: React.FC = () => {
  // Data state
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);

  // Create form state
  const [createName, setCreateName] = useState('');
  const [createSlug, setCreateSlug] = useState('');
  const [createAutoSlug, setCreateAutoSlug] = useState(true);
  const [createSubmitting, setCreateSubmitting] = useState(false);

  // Edit form state
  const [editName, setEditName] = useState('');
  const [editSlug, setEditSlug] = useState('');
  const [editStatus, setEditStatus] = useState<'active' | 'inactive' | 'suspended'>('active');
  const [editSubmitting, setEditSubmitting] = useState(false);

  // Delete state
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  // Toast state
  const [toast, setToast] = useState<ToastState | null>(null);

  // Permission checking
  const { hasPermission } = useAuth();

  // CSS variable styles for theming
  const cardStyle = { backgroundColor: 'var(--color-background-card, #ffffff)' };
  const textPrimaryStyle = { color: 'var(--color-text-primary, #111827)' };
  const textSecondaryStyle = { color: 'var(--color-text-secondary, #6b7280)' };
  const borderStyle = { borderColor: 'var(--color-border-default, #e5e7eb)' };

  /**
   * Fetch organizations from the backend
   */
  const fetchOrganizations = useCallback(async () => {
    try {
      setLoading(true);
      const response = await organizationsService.getOrganizations(page, 20);
      setOrganizations(response.data);
      setTotalPages(response.pagination.pages);
      setTotal(response.pagination.total);
      setError(null);
    } catch (err) {
      logger.error('Failed to fetch organizations', err);
      setError('Failed to load organizations. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [page]);

  // Load organizations on mount and when page changes
  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  /**
   * Handle create name change with auto-slug generation
   */
  const handleCreateNameChange = (value: string) => {
    setCreateName(value);
    if (createAutoSlug) {
      setCreateSlug(generateSlug(value));
    }
  };

  /**
   * Handle create slug change (disables auto-slug)
   */
  const handleCreateSlugChange = (value: string) => {
    setCreateSlug(value);
    setCreateAutoSlug(false);
  };

  /**
   * Handle opening the create modal
   */
  const handleOpenCreate = () => {
    setCreateName('');
    setCreateSlug('');
    setCreateAutoSlug(true);
    setShowCreateModal(true);
  };

  /**
   * Handle create organization form submission
   */
  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!createName.trim() || !createSlug.trim()) return;

    setCreateSubmitting(true);
    try {
      const data: CreateOrganizationDto = {
        name: createName.trim(),
        slug: createSlug.trim(),
      };
      const newOrg = await organizationsService.createOrganization(data);
      setOrganizations((prev) => [...prev, newOrg]);
      setToast({ message: `Organization "${newOrg.name}" created successfully.`, type: 'success' });
      setShowCreateModal(false);
    } catch (err) {
      logger.error('Failed to create organization', err);
      setToast({ message: 'Failed to create organization. Please try again.', type: 'error' });
    } finally {
      setCreateSubmitting(false);
    }
  };

  /**
   * Handle opening the edit modal
   */
  const handleOpenEdit = (org: Organization) => {
    setSelectedOrg(org);
    setEditName(org.name);
    setEditSlug(org.slug);
    setEditStatus(org.status);
    setShowEditModal(true);
  };

  /**
   * Handle edit organization form submission
   */
  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedOrg || !editName.trim() || !editSlug.trim()) return;

    setEditSubmitting(true);
    try {
      const data: UpdateOrganizationDto = {
        name: editName.trim(),
        slug: editSlug.trim(),
        status: editStatus,
      };
      const updated = await organizationsService.updateOrganization(selectedOrg.id, data);
      setOrganizations((prev) =>
        prev.map((o) => (o.id === updated.id ? updated : o))
      );
      setToast({ message: `Organization "${updated.name}" updated successfully.`, type: 'success' });
      setShowEditModal(false);
      setSelectedOrg(null);
    } catch (err) {
      logger.error('Failed to update organization', err);
      setToast({ message: 'Failed to update organization. Please try again.', type: 'error' });
    } finally {
      setEditSubmitting(false);
    }
  };

  /**
   * Handle opening the delete dialog
   */
  const handleOpenDelete = (org: Organization) => {
    setSelectedOrg(org);
    setShowDeleteDialog(true);
  };

  /**
   * Handle deleting an organization
   */
  const handleDelete = async () => {
    if (!selectedOrg) return;

    setDeleteSubmitting(true);
    try {
      await organizationsService.deleteOrganization(selectedOrg.id);
      setOrganizations((prev) => prev.filter((o) => o.id !== selectedOrg.id));
      setToast({ message: `Organization "${selectedOrg.name}" deleted successfully.`, type: 'success' });
      setShowDeleteDialog(false);
      setSelectedOrg(null);
    } catch (err) {
      logger.error('Failed to delete organization', err);
      setToast({ message: 'Failed to delete organization. Please try again.', type: 'error' });
    } finally {
      setDeleteSubmitting(false);
    }
  };

  /**
   * Close all modals
   */
  const handleCloseModals = () => {
    setShowCreateModal(false);
    setShowEditModal(false);
    setShowDeleteDialog(false);
    setSelectedOrg(null);
  };

  return (
    <Container className="py-8" data-testid="organizations-page">
      {/* Page Header */}
      <div className="page-header page-header--with-actions">
        <div>
          <h1 className="page-title">Organizations</h1>
          <p className="page-subtitle">
            Manage organizations and their settings.
          </p>
        </div>
        <Button
          variant="primary"
          onClick={handleOpenCreate}
          data-testid="create-organization-button"
        >
          Create Organization
        </Button>
      </div>

      {/* Error State */}
      {error && (
        <div
          className="mb-6 p-4 rounded-md border"
          style={{
            backgroundColor: 'var(--color-status-error-light, #fef2f2)',
            borderColor: 'var(--color-error, #ef4444)',
          }}
        >
          <p className="text-sm" style={{ color: 'var(--color-error, #ef4444)' }}>{error}</p>
          <button
            type="button"
            className="mt-2 text-sm underline hover:no-underline"
            style={{ color: 'var(--color-error, #ef4444)' }}
            onClick={fetchOrganizations}
          >
            Retry
          </button>
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="rounded-lg shadow-sm border p-8" style={{ ...cardStyle, ...borderStyle }}>
          <div className="flex items-center justify-center">
            <div
              className="animate-spin rounded-full h-8 w-8 border-b-2"
              style={{ borderColor: 'var(--color-primary, #3b82f6)' }}
            />
            <span className="ml-3" style={textSecondaryStyle}>Loading organizations...</span>
          </div>
        </div>
      ) : (
        <>
          {/* Organizations Table */}
          <div className="rounded-lg shadow-sm border overflow-hidden" style={{ ...cardStyle, ...borderStyle }}>
            <table className="min-w-full divide-y" style={{ borderColor: 'var(--color-border-light, #f3f4f6)' }}>
              <thead style={{ backgroundColor: 'var(--color-background-page, #f9fafb)' }}>
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={textSecondaryStyle}>
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={textSecondaryStyle}>
                    Slug
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={textSecondaryStyle}>
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={textSecondaryStyle}>
                    Created
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider" style={textSecondaryStyle}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody style={cardStyle}>
                {organizations.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-8 text-center" style={textSecondaryStyle}>
                      No organizations found. Click "Create Organization" to add one.
                    </td>
                  </tr>
                ) : (
                  organizations.map((org) => (
                    <tr key={org.id} style={cardStyle} data-testid={`org-row-${org.id}`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div
                            className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center"
                            style={{ backgroundColor: 'var(--color-primary-light, #dbeafe)' }}
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              aria-hidden="true"
                              style={{ color: 'var(--color-primary, #3b82f6)' }}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                              />
                            </svg>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium" style={textPrimaryStyle}>
                              {org.name}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <code
                          className="text-xs px-2 py-1 rounded"
                          style={{
                            backgroundColor: 'var(--color-background-surface, #f3f4f6)',
                            color: 'var(--color-text-primary, #111827)',
                          }}
                        >
                          {org.slug}
                        </code>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Badge variant={getStatusBadgeVariant(org.status)} size="sm">
                          {org.status}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm" style={textSecondaryStyle}>
                          {formatDate(org.created_at)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenEdit(org)}
                            data-testid={`edit-org-${org.id}`}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleOpenDelete(org)}
                            data-testid={`delete-org-${org.id}`}
                          >
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div
              className="flex items-center justify-between mt-4 px-4 py-3 rounded-lg border"
              style={{ ...cardStyle, ...borderStyle }}
            >
              <Button
                variant="ghost"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                data-testid="org-pagination-prev"
              >
                Previous
              </Button>
              <span className="text-sm" style={textSecondaryStyle}>
                Page {page} of {totalPages} ({total} total)
              </span>
              <Button
                variant="ghost"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                data-testid="org-pagination-next"
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}

      {/* Create Organization Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="fixed inset-0"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
            onClick={handleCloseModals}
          />
          {/* Modal */}
          <div
            className="relative z-10 w-full max-w-lg mx-4 rounded-xl shadow-xl p-6 border"
            style={{
              backgroundColor: 'var(--color-background-modal, #ffffff)',
              borderColor: 'var(--color-border-default, #e5e7eb)',
            }}
          >
            <form onSubmit={handleCreateSubmit}>
              <h2 className="text-lg font-semibold mb-4" style={textPrimaryStyle}>
                Create Organization
              </h2>

              {/* Name Input */}
              <div className="mb-4">
                <label
                  htmlFor="org-name"
                  className="block text-sm font-medium mb-1"
                  style={textSecondaryStyle}
                >
                  Name
                </label>
                <input
                  id="org-name"
                  type="text"
                  required
                  value={createName}
                  onChange={(e) => handleCreateNameChange(e.target.value)}
                  placeholder="e.g., Acme Corporation"
                  className="w-full px-3 py-2 rounded-lg border text-sm focus:ring-2 focus:outline-none"
                  style={{
                    backgroundColor: 'var(--color-background-input, #ffffff)',
                    color: 'var(--color-text-primary, #111827)',
                    borderColor: 'var(--color-border-default, #e5e7eb)',
                  }}
                  data-testid="create-org-name"
                />
              </div>

              {/* Slug Input */}
              <div className="mb-6">
                <label
                  htmlFor="org-slug"
                  className="block text-sm font-medium mb-1"
                  style={textSecondaryStyle}
                >
                  Slug
                </label>
                <input
                  id="org-slug"
                  type="text"
                  required
                  value={createSlug}
                  onChange={(e) => handleCreateSlugChange(e.target.value)}
                  placeholder="e.g., acme-corporation"
                  className="w-full px-3 py-2 rounded-lg border text-sm font-mono focus:ring-2 focus:outline-none"
                  style={{
                    backgroundColor: 'var(--color-background-input, #ffffff)',
                    color: 'var(--color-text-primary, #111827)',
                    borderColor: 'var(--color-border-default, #e5e7eb)',
                  }}
                  data-testid="create-org-slug"
                />
                <p className="mt-1 text-xs" style={{ color: 'var(--color-text-muted, #9ca3af)' }}>
                  URL-friendly identifier. Auto-generated from the name.
                </p>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3">
                <Button
                  variant="ghost"
                  onClick={handleCloseModals}
                  type="button"
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  type="submit"
                  disabled={createSubmitting || !createName.trim() || !createSlug.trim()}
                  data-testid="submit-create-org"
                >
                  {createSubmitting ? 'Creating...' : 'Create Organization'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Organization Modal */}
      {showEditModal && selectedOrg && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="fixed inset-0"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
            onClick={handleCloseModals}
          />
          {/* Modal */}
          <div
            className="relative z-10 w-full max-w-lg mx-4 rounded-xl shadow-xl p-6 border"
            style={{
              backgroundColor: 'var(--color-background-modal, #ffffff)',
              borderColor: 'var(--color-border-default, #e5e7eb)',
            }}
          >
            <form onSubmit={handleEditSubmit}>
              <h2 className="text-lg font-semibold mb-4" style={textPrimaryStyle}>
                Edit Organization
              </h2>

              {/* Name Input */}
              <div className="mb-4">
                <label
                  htmlFor="edit-org-name"
                  className="block text-sm font-medium mb-1"
                  style={textSecondaryStyle}
                >
                  Name
                </label>
                <input
                  id="edit-org-name"
                  type="text"
                  required
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border text-sm focus:ring-2 focus:outline-none"
                  style={{
                    backgroundColor: 'var(--color-background-input, #ffffff)',
                    color: 'var(--color-text-primary, #111827)',
                    borderColor: 'var(--color-border-default, #e5e7eb)',
                  }}
                  data-testid="edit-org-name"
                />
              </div>

              {/* Slug Input */}
              <div className="mb-4">
                <label
                  htmlFor="edit-org-slug"
                  className="block text-sm font-medium mb-1"
                  style={textSecondaryStyle}
                >
                  Slug
                </label>
                <input
                  id="edit-org-slug"
                  type="text"
                  required
                  value={editSlug}
                  onChange={(e) => setEditSlug(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border text-sm font-mono focus:ring-2 focus:outline-none"
                  style={{
                    backgroundColor: 'var(--color-background-input, #ffffff)',
                    color: 'var(--color-text-primary, #111827)',
                    borderColor: 'var(--color-border-default, #e5e7eb)',
                  }}
                  data-testid="edit-org-slug"
                />
              </div>

              {/* Status Select */}
              <div className="mb-6">
                <label
                  htmlFor="edit-org-status"
                  className="block text-sm font-medium mb-1"
                  style={textSecondaryStyle}
                >
                  Status
                </label>
                <select
                  id="edit-org-status"
                  value={editStatus}
                  onChange={(e) => setEditStatus(e.target.value as 'active' | 'inactive' | 'suspended')}
                  className="w-full px-3 py-2 rounded-lg border text-sm focus:ring-2 focus:outline-none"
                  style={{
                    backgroundColor: 'var(--color-background-input, #ffffff)',
                    color: 'var(--color-text-primary, #111827)',
                    borderColor: 'var(--color-border-default, #e5e7eb)',
                  }}
                  data-testid="edit-org-status"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3">
                <Button
                  variant="ghost"
                  onClick={handleCloseModals}
                  type="button"
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  type="submit"
                  disabled={editSubmitting || !editName.trim() || !editSlug.trim()}
                  data-testid="submit-edit-org"
                >
                  {editSubmitting ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && selectedOrg && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="fixed inset-0"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
            onClick={handleCloseModals}
          />
          {/* Dialog */}
          <div
            className="relative z-10 w-full max-w-md mx-4 rounded-xl shadow-xl p-6 border"
            style={{
              backgroundColor: 'var(--color-background-modal, #ffffff)',
              borderColor: 'var(--color-border-default, #e5e7eb)',
            }}
          >
            <h2 className="text-lg font-semibold mb-2" style={textPrimaryStyle}>
              Delete Organization
            </h2>
            <p className="text-sm mb-6" style={textSecondaryStyle}>
              Are you sure you want to delete the organization{' '}
              <strong style={textPrimaryStyle}>"{selectedOrg.name}"</strong>?
              This action cannot be undone. All associated data will be permanently removed.
            </p>
            <div className="flex justify-end gap-3">
              <Button
                variant="ghost"
                onClick={handleCloseModals}
                type="button"
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDelete}
                disabled={deleteSubmitting}
                data-testid="confirm-delete-org"
              >
                {deleteSubmitting ? 'Deleting...' : 'Delete Organization'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notifications */}
      {toast && (
        <div className="fixed bottom-4 right-4 z-50">
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
            data-testid="organizations-toast"
          />
        </div>
      )}
    </Container>
  );
};

export default OrganizationsPage;
